<template>
    <list-item-with-title
	      ref="psystemEl"
        :title="$t('requisite.psystem-name-input.title')"
        :media="requisite?.psystem?.imgPath ?? undefined"
        :text="requisite?.psystem?.name ?? ''"
        :placeholder="$t('requisite.psystem-name-input.placeholder')"
        :loading="loading"
        @on-item-click="handleItemClick"
    />
    <list-input-with-title
        ref="addressEl"
        :title="$t('requisite.address-input.title')"
        :placeholder="addressTypePlaceholder"
        :required="true"
        v-model:text="localRequisite.address"
        @update:text="(value) => {handleUpdateRequisiteProp('address', value)}"
        :disabled="!localRequisite.psystem"
    >
	    <template #after-title>
		    <RequisitePageP2PRequisiteAddressTypeToggle
			    :requisite="localRequisite"
			    :address-types="controller.addressTypes.value"
			    @select="handleSelectAddressType"
		    />
	    </template>
    </list-input-with-title>
    <list-input-with-title
        ref="nameEl"
        :title="$t('requisite.name-input.title')"
        v-model:text="localRequisite.name"
        @update:text="(value) => {handleUpdateRequisiteProp('name', value)}"
        :placeholder="$t('requisite.name-input.placeholder')"
        :required="true"
        :hint="$t('requisite.name-input.hint')"
    />
    <list-input-with-title
        :title="$t('requisite.comment-input.title')"
        :placeholder="$t('requisite.comment-input.placeholder')"
        v-model:text="localRequisite.comment"
        @update:text="(value) => {handleUpdateRequisiteProp('comment', value)}"
        :textarea="true"
        :hint="$t('requisite.comment-input.hint')"
    />
</template>

<script setup lang="ts">

import ListItemWithTitle from "@components/atomic/list-item/ListItemWithTitle.vue";
import {computed, onMounted, ref} from "vue";
import Requisite from "@models/requisite/Requisite";
import ListInputWithTitle from "@components/atomic/list-item/ListInputWithTitle.vue";
import RequisitePageController from "@/views/requisites-group/requisite/RequisitePageController";
import RequisitePageP2PRequisiteAddressTypeToggle
	from "@/views/requisites-group/requisite/components/RequisitePageP2PRequisiteAddressTypeToggle.vue";
import {RequisiteAddressType} from "@enums/RequisiteAddressType";
import {useTranslate} from "@tolgee/vue";

interface Props {
	requisite: Requisite | null;
	loading?: boolean;
}

interface Emits {
	(e: 'toSelectPsystem'): void,
	(e: "update:requisite", value: Requisite): void,
}

const { t } = useTranslate();

const props = defineProps<Props>();
const addressEl = ref(null);
const nameEl = ref(null);
const psystemEl = ref(null);

const emits = defineEmits<Emits>();

defineExpose({ addressEl, nameEl, psystemEl });

const controller = RequisitePageController.of();

const { defaultPSystems } = controller;

const handleItemClick = () => {
    emits('toSelectPsystem');
}

const localRequisite = ref<Requisite>(new Requisite());
localRequisite.value.psystem = defaultPSystems.p2p;

const handleUpdateRequisiteProp = (prop: 'name' | 'comment' | 'address', value: string) => {
	localRequisite.value[prop] = value;
	emits('update:requisite', localRequisite.value)
}

const handleSelectAddressType = (value: { id: number | null, slug: RequisiteAddressType}) => {
	localRequisite.value.addressTypeId = value.id;
	emits('update:requisite', localRequisite.value)
}

const addressTypePlaceholder = computed(() => {
	const addressTypeId = localRequisite.value.addressTypeId;

	const activeAddressType = controller.addressTypes.value.find((type) => type.id === addressTypeId);

	if (activeAddressType) {
		switch (activeAddressType.slug) {
			case RequisiteAddressType.CARD:
				return t.value('requisite.address-input.placeholder-card');
			case RequisiteAddressType.PHONE:
				return t.value('requisite.address-input.placeholder-phone');
		}
	}

	return t.value('requisite.address-input.placeholder');
})

onMounted(() => {
	if (props.requisite) {
		localRequisite.value = props.requisite;
	}
})
</script>
